import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  createLogbookComment,
  deleteLogbookComment,
  fetchLogbookComments,
} from '../../utils/logbookApi';

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMemberDisplayName = (value) => {
  if (typeof value !== 'string') return 'Socio';
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (!normalized) return 'Socio';
  const parts = normalized.split(' ').filter(Boolean);
  const firstName = parts[0] || 'Socio';
  const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
  const lastInitial = lastName ? `${lastName.charAt(0).toUpperCase()}.` : '';
  return lastInitial ? `${firstName} ${lastInitial}` : firstName;
};

const normalizeMember = (member) => {
  if (!member || typeof member !== 'object') return null;
  const memberId =
    typeof member.documentId === 'string' ? member.documentId.trim() : '';
  if (!memberId) return null;
  const firstName =
    typeof member.nomeSocio === 'string' ? member.nomeSocio.trim() : '';
  const lastName =
    typeof member.cognomeSocio === 'string' ? member.cognomeSocio.trim() : '';
  return {
    memberId,
    memberName: `${firstName} ${lastName}`.trim() || 'Socio',
  };
};

const extractSessionMembers = (userData) => {
  if (!userData || !Array.isArray(userData.tesseramenti)) return [];
  const unique = new Map();
  userData.tesseramenti.forEach((member) => {
    const normalized = normalizeMember(member);
    if (!normalized) return;
    unique.set(normalized.memberId, normalized);
  });
  return Array.from(unique.values());
};

const CommentsTree = ({
  itemsByParent,
  parentId,
  depth,
  onReplyStart,
  onReplyCancel,
  onReplySubmit,
  replyTextByParent,
  setReplyTextByParent,
  replyMemberByParent,
  setReplyMemberByParent,
  members,
  isMemberSelectionRequired,
  isSubmitting,
  canWrite,
  canDelete,
  onDelete,
}) => {
  const nodes = itemsByParent.get(parentId) || [];
  if (!nodes.length) return null;

  return (
    <VStack spacing={4} align="stretch">
      {nodes.map((comment) => {
        const replyText = replyTextByParent[comment.id] || '';
        const replyMemberId = replyMemberByParent[comment.id] || '';
        const hasReplyBox = Object.prototype.hasOwnProperty.call(
          replyTextByParent,
          comment.id
        );
        return (
          <Box
            key={comment.id}
            pl={depth > 0 ? 4 : 0}
            borderLeftWidth={depth > 0 ? '2px' : '0'}
            borderColor={depth > 0 ? 'gray.100' : 'transparent'}
          >
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              bg="gray.50"
            >
              <HStack justify="space-between" align="start">
                <Box>
                  <Text fontWeight="semibold" color="gray.800">
                    {formatMemberDisplayName(comment.memberName)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {formatDateTime(comment.createdAt)}
                  </Text>
                </Box>
                <HStack spacing={2}>
                  {canWrite ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() =>
                        hasReplyBox
                          ? onReplyCancel(comment.id)
                          : onReplyStart(comment.id)
                      }
                    >
                      Rispondi
                    </Button>
                  ) : null}
                  {canDelete(comment) ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => onDelete(comment.id)}
                      isDisabled={isSubmitting}
                    >
                      Elimina
                    </Button>
                  ) : null}
                </HStack>
              </HStack>

              <Text
                mt={3}
                fontSize="sm"
                color={comment.isDeleted ? 'gray.500' : 'gray.700'}
                fontStyle={comment.isDeleted ? 'italic' : 'normal'}
              >
                {comment.isDeleted ? 'Commento eliminato' : comment.content}
              </Text>

              {hasReplyBox ? (
                <Box mt={3}>
                  {isMemberSelectionRequired ? (
                    <FormControl isRequired mb={3}>
                      <FormLabel fontSize="xs">Socio per la risposta</FormLabel>
                      <Select
                        placeholder="Seleziona un socio"
                        value={replyMemberId}
                        onChange={(event) =>
                          setReplyMemberByParent((prev) => ({
                            ...prev,
                            [comment.id]: event.target.value,
                          }))
                        }
                        bg="white"
                        size="sm"
                      >
                        {members.map((member) => (
                          <option key={member.memberId} value={member.memberId}>
                            {formatMemberDisplayName(member.memberName)}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                  <Textarea
                    value={replyText}
                    onChange={(event) =>
                      setReplyTextByParent((prev) => ({
                        ...prev,
                        [comment.id]: event.target.value,
                      }))
                    }
                    placeholder="Scrivi una risposta..."
                    rows={3}
                    bg="white"
                  />
                  <HStack mt={2} justify="end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onReplyCancel(comment.id)}
                    >
                      Annulla
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="teal"
                      onClick={() => onReplySubmit(comment.id, replyMemberId)}
                      isLoading={isSubmitting}
                      isDisabled={isMemberSelectionRequired && !replyMemberId}
                    >
                      Invia risposta
                    </Button>
                  </HStack>
                </Box>
              ) : null}
            </Box>

            <Box mt={3}>
              <CommentsTree
                itemsByParent={itemsByParent}
                parentId={comment.id}
                depth={depth + 1}
                onReplyStart={onReplyStart}
                onReplyCancel={onReplyCancel}
                onReplySubmit={onReplySubmit}
                replyTextByParent={replyTextByParent}
                setReplyTextByParent={setReplyTextByParent}
                replyMemberByParent={replyMemberByParent}
                setReplyMemberByParent={setReplyMemberByParent}
                members={members}
                isMemberSelectionRequired={isMemberSelectionRequired}
                isSubmitting={isSubmitting}
                canWrite={canWrite}
                canDelete={canDelete}
                onDelete={onDelete}
              />
            </Box>
          </Box>
        );
      })}
    </VStack>
  );
};

const LogbookCommentsSection = ({ slug }) => {
  const { isAuthenticated, userData } = useAuth();
  const toast = useToast();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [replyTextByParent, setReplyTextByParent] = useState({});
  const [replyMemberByParent, setReplyMemberByParent] = useState({});
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);

  const isMemberSelectionRequired = members.length > 1;
  const availableMemberIds = useMemo(
    () => new Set(members.map((member) => member.memberId)),
    [members]
  );

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchLogbookComments(slug);
      setComments(Array.isArray(response.comments) ? response.comments : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Errore nel caricamento dei commenti');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadMembers = useCallback(async () => {
    if (!isAuthenticated || !userData?.id) {
      setMembers([]);
      setSelectedMemberId('');
      return;
    }

    const fromSession = extractSessionMembers(userData);
    if (fromSession.length > 0) {
      setMembers(fromSession);
      setSelectedMemberId(
        fromSession.length === 1 ? fromSession[0].memberId : ''
      );
      return;
    }

    try {
      setLoadingMembers(true);
      const response = await fetch(
        `/api/soci?userId=${encodeURIComponent(userData.id)}`
      );
      if (!response.ok) {
        throw new Error('Errore nel recupero soci');
      }
      const payload = await response.json();
      const fromApi = Array.isArray(payload.data)
        ? payload.data.map(normalizeMember).filter(Boolean)
        : [];
      setMembers(fromApi);
      setSelectedMemberId(fromApi.length === 1 ? fromApi[0].memberId : '');
    } catch (err) {
      setMembers([]);
      setSelectedMemberId('');
      toast({
        title: 'Impossibile caricare i soci',
        description: err.message || 'Riprova più tardi',
        status: 'warning',
        isClosable: true,
      });
    } finally {
      setLoadingMembers(false);
    }
  }, [isAuthenticated, toast, userData]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const commentsByParent = useMemo(() => {
    const grouped = new Map();
    comments
      .slice()
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .forEach((comment) => {
        const key = comment.parentId || null;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(comment);
      });
    return grouped;
  }, [comments]);

  const validateBeforeSubmit = (memberId) => {
    if (!isAuthenticated) {
      toast({
        title: 'Accesso richiesto',
        description: 'Solo i soci autenticati possono commentare.',
        status: 'warning',
        isClosable: true,
      });
      return false;
    }
    if (!memberId) {
      toast({
        title: 'Seleziona un socio',
        description: 'La scelta del socio è obbligatoria prima dell’invio.',
        status: 'warning',
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const submitComment = async ({
    content,
    parentId = null,
    memberIdOverride = null,
  }) => {
    const memberIdToUse = memberIdOverride || selectedMemberId;
    if (!validateBeforeSubmit(memberIdToUse)) return;
    const sanitized = typeof content === 'string' ? content.trim() : '';
    if (!sanitized) return;

    try {
      setSubmitting(true);
      const response = await createLogbookComment({
        slug,
        memberId: memberIdToUse,
        content: sanitized,
        parentId,
      });
      setComments((prev) => [...prev, response.comment]);
      if (parentId) {
        setReplyTextByParent((prev) => {
          const next = { ...prev };
          delete next[parentId];
          return next;
        });
        setReplyMemberByParent((prev) => {
          const next = { ...prev };
          delete next[parentId];
          return next;
        });
      } else {
        setCommentText('');
      }
      if (isMemberSelectionRequired) {
        setSelectedMemberId('');
      }
    } catch (err) {
      toast({
        title: 'Commento non inviato',
        description: err.message || 'Errore durante il salvataggio',
        status: 'error',
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      setSubmitting(true);
      await deleteLogbookComment({ slug, commentId });
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                isDeleted: true,
                deletedAt: new Date().toISOString(),
                content: '',
              }
            : comment
        )
      );
    } catch (err) {
      toast({
        title: 'Impossibile eliminare il commento',
        description: err.message || 'Errore durante l’eliminazione',
        status: 'error',
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canDelete = (comment) =>
    availableMemberIds.has(comment.memberId) && !comment.isDeleted;

  return (
    <Box as="section" aria-labelledby="logbook-comments-heading" mt={12}>
      <Heading
        id="logbook-comments-heading"
        as="h2"
        size="md"
        color="#3b235a"
        mb={4}
      >
        Commenti
      </Heading>

      {!isAuthenticated ? (
        <Alert status="info" mb={6} borderRadius="md">
          <AlertIcon />
          Effettua il login per inserire un commento come socio.
        </Alert>
      ) : null}

      {loading ? (
        <HStack spacing={2} color="gray.500">
          <Spinner size="sm" />
          <Text>Caricamento commenti...</Text>
        </HStack>
      ) : null}

      {!loading && error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      ) : null}

      {!loading && !error && comments.length === 0 ? (
        <Text color="gray.500">Nessun commento per questo articolo.</Text>
      ) : null}

      {!loading && !error && comments.length > 0 ? (
        <CommentsTree
          itemsByParent={commentsByParent}
          parentId={null}
          depth={0}
          onReplyStart={(commentId) => {
            setReplyTextByParent((prev) => ({
              ...prev,
              [commentId]: prev[commentId] || '',
            }));
            setReplyMemberByParent((prev) => ({
              ...prev,
              [commentId]:
                prev[commentId] ||
                (members.length === 1 ? members[0].memberId : ''),
            }));
          }}
          onReplyCancel={(commentId) => {
            setReplyTextByParent((prev) => {
              const next = { ...prev };
              delete next[commentId];
              return next;
            });
            setReplyMemberByParent((prev) => {
              const next = { ...prev };
              delete next[commentId];
              return next;
            });
          }}
          onReplySubmit={(commentId, memberId) =>
            submitComment({
              content: replyTextByParent[commentId] || '',
              parentId: commentId,
              memberIdOverride: memberId,
            })
          }
          replyTextByParent={replyTextByParent}
          setReplyTextByParent={setReplyTextByParent}
          replyMemberByParent={replyMemberByParent}
          setReplyMemberByParent={setReplyMemberByParent}
          members={members}
          isMemberSelectionRequired={isMemberSelectionRequired}
          isSubmitting={submitting}
          canWrite={isAuthenticated}
          canDelete={canDelete}
          onDelete={handleDelete}
        />
      ) : null}

      {isAuthenticated ? (
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          mt={6}
        >
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Socio</FormLabel>
              <Select
                placeholder={
                  loadingMembers ? 'Caricamento soci...' : 'Seleziona un socio'
                }
                value={selectedMemberId}
                onChange={(event) => setSelectedMemberId(event.target.value)}
                isDisabled={loadingMembers || members.length === 0}
                bg="white"
              >
                {members.map((member) => (
                  <option key={member.memberId} value={member.memberId}>
                    {formatMemberDisplayName(member.memberName)}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Nuovo commento</FormLabel>
              <Textarea
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                placeholder="Scrivi qui il tuo commento..."
                rows={4}
                bg="white"
              />
            </FormControl>

            <HStack justify="end">
              <Button
                colorScheme="teal"
                onClick={() => submitComment({ content: commentText })}
                isLoading={submitting}
                isDisabled={
                  loadingMembers ||
                  !members.length ||
                  (isMemberSelectionRequired && !selectedMemberId)
                }
              >
                Pubblica commento
              </Button>
            </HStack>
            {isMemberSelectionRequired && !selectedMemberId ? (
              <Text fontSize="sm" color="red.500" textAlign="right">
                La scelta del socio è obbligatoria prima dell’invio.
              </Text>
            ) : null}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
};

export default LogbookCommentsSection;
